import { createSlice } from "@reduxjs/toolkit";
import { getProducts, addNewProduct, updateProduct, deleteProducts, getOrders, addNewOrder, updateOrder, deleteOrder, getCustomers, addNewCustomer, updateCustomer, deleteCustomer, getSellers, getSeller, addNewSeller, updateSeller, deleteSeller, suspendSeller, getEcommerceCategories, addNewEcommerceCategory, updateEcommerceCategory, deleteEcommerceCategory, getSubcategories, addNewSubcategory, updateSubcategory, deleteSubcategory, getAttributes, addNewAttribute, updateAttribute, deleteAttribute, getCommissions, addNewCommission, updateCommission, deleteCommission, getTaxes, addNewTax, updateTax, deleteTax } from './thunk';
export const initialState = {
  products: [],
  orders: [],
  sellers: [],
  currentSeller: null,
  customers: [],
  categories: [],
  subcategories: [],
  attributes: [],
  commissions: [],
  taxes: [],
  error: {},
};

const EcommerceSlice = createSlice({
  name: 'EcommerceSlice',
  initialState,
  reducer: {},
  extraReducers: (builder) => {
    builder.addCase(getProducts.fulfilled, (state, action) => {
      state.products = action.payload?.data || action.payload || [];
    });

    builder.addCase(getProducts.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.products = []; // Ensure products is always an array
    });

    builder.addCase(addNewProduct.fulfilled, (state, action) => {
      // Ensure products is always an array
      if (!Array.isArray(state.products)) {
        state.products = [];
      }
      const newProduct = action.payload?.data || action.payload;
      if (newProduct) {
        state.products.unshift(newProduct);
      }
    });

    builder.addCase(addNewProduct.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateProduct.fulfilled, (state, action) => {
      if (!state.products) {
        state.products = [];
      }
      const updatedProduct = action.payload?.data || action.payload;
      if (updatedProduct) {
        state.products = state.products.map(product =>
          (product._id || product.id)?.toString() === (updatedProduct._id || updatedProduct.id)?.toString()
            ? { ...product, ...updatedProduct }
            : product
        );
      }
    });

    builder.addCase(updateProduct.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteProducts.fulfilled, (state, action) => {
      // Ensure products is always an array
      if (!Array.isArray(state.products)) {
        state.products = [];
      }
      const productId = action.payload?.product || action.payload;
      if (productId) {
        state.products = state.products.filter((product) => 
          (product._id || product.id)?.toString() !== productId.toString()
        );
      }
    });

    builder.addCase(deleteProducts.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(getOrders.fulfilled, (state, action) => {
      state.orders = action.payload?.data || action.payload || [];
      state.isOrderCreated = false;
      state.isOrderSuccess = true;
    });

    builder.addCase(getOrders.rejected, (state, action) => {
      state.error = action.payload?.error || null;
      state.isOrderCreated = false;
      state.isOrderSuccess = false;
      state.orders = []; // Ensure orders is always an array
    });

    builder.addCase(addNewOrder.fulfilled, (state, action) => {
      // Ensure orders is always an array
      if (!Array.isArray(state.orders)) {
        state.orders = [];
      }
      const newOrder = action.payload?.data || action.payload;
      if (newOrder) {
        state.orders.unshift(newOrder);
      }
      state.isOrderCreated = true;
    });

    builder.addCase(addNewOrder.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateOrder.fulfilled, (state, action) => {
      if (!state.orders) {
        state.orders = [];
      }
      const updatedOrder = action.payload?.data || action.payload;
      if (updatedOrder) {
        state.orders = state.orders.map(order =>
          (order._id || order.id)?.toString() === (updatedOrder._id || updatedOrder.id)?.toString()
            ? { ...order, ...updatedOrder }
            : order
        );
      }
    });

    builder.addCase(updateOrder.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteOrder.fulfilled, (state, action) => {
      // Ensure orders is always an array
      if (!Array.isArray(state.orders)) {
        state.orders = [];
      }
      const orderId = action.payload?.order || action.payload;
      if (orderId) {
        state.orders = state.orders.filter(
          order => (order._id || order.id)?.toString() !== orderId.toString()
        );
      }
    });

    builder.addCase(deleteOrder.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(getSellers.fulfilled, (state, action) => {
      // Ensure we always set an array
      const payload = action.payload;
      if (Array.isArray(payload)) {
        state.sellers = payload;
      } else if (payload && Array.isArray(payload.data)) {
        state.sellers = payload.data;
      } else if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        // If payload is an object but not an array, wrap it
        state.sellers = [payload];
      } else {
        state.sellers = [];
      }
    });

    builder.addCase(getSellers.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.sellers = [];
    });

    builder.addCase(addNewSeller.fulfilled, (state, action) => {
      // Ensure sellers is always an array
      if (!Array.isArray(state.sellers)) {
        state.sellers = [];
      }
      const newSeller = action.payload?.data || action.payload;
      if (newSeller) {
        state.sellers.unshift(newSeller);
      }
    });

    builder.addCase(addNewSeller.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateSeller.fulfilled, (state, action) => {
      if (!Array.isArray(state.sellers)) {
        state.sellers = [];
      }
      const updatedSeller = action.payload?.data || action.payload;
      if (updatedSeller) {
        state.sellers = state.sellers.map(seller =>
          (seller._id || seller.id)?.toString() === (updatedSeller._id || updatedSeller.id)?.toString()
            ? { ...seller, ...updatedSeller }
            : seller
        );
      }
    });

    builder.addCase(updateSeller.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteSeller.fulfilled, (state, action) => {
      // Ensure sellers is always an array
      if (!Array.isArray(state.sellers)) {
        state.sellers = [];
      }
      const sellerId = action.payload?.seller || action.payload?.id || action.payload;
      if (sellerId) {
        state.sellers = state.sellers.filter(
          seller => (seller._id || seller.id)?.toString() !== sellerId.toString()
        );
      }
    });

    builder.addCase(deleteSeller.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(suspendSeller.fulfilled, (state, action) => {
      if (!Array.isArray(state.sellers)) {
        state.sellers = [];
      }
      const updatedSeller = action.payload?.data || action.payload;
      if (updatedSeller) {
        state.sellers = state.sellers.map(seller =>
          (seller._id || seller.id)?.toString() === (updatedSeller._id || updatedSeller.id)?.toString()
            ? { ...seller, ...updatedSeller }
            : seller
        );
      }
    });

    builder.addCase(suspendSeller.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(getSeller.fulfilled, (state, action) => {
      const payload = action.payload;
      if (payload && typeof payload === 'object' && !payload.error) {
        // Handle both wrapped and unwrapped responses
        state.currentSeller = payload.data || payload;
      } else {
        state.currentSeller = null;
      }
    });

    builder.addCase(getSeller.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.currentSeller = null;
    });

    builder.addCase(getCustomers.fulfilled, (state, action) => {
      state.customers = action.payload?.data || action.payload || [];
      state.isCustomerCreated = false;
      state.isCustomerSuccess = true;
    });

    builder.addCase(getCustomers.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.isCustomerCreated = false;
      state.isCustomerSuccess = false;
      state.customers = []; // Ensure customers is always an array
    });

    builder.addCase(addNewCustomer.fulfilled, (state, action) => {
      // Ensure customers is always an array
      if (!Array.isArray(state.customers)) {
        state.customers = [];
      }
      const newCustomer = action.payload?.data || action.payload;
      if (newCustomer) {
        state.customers.unshift(newCustomer);
      }
      state.isCustomerCreated = true;
    });
    builder.addCase(addNewCustomer.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateCustomer.fulfilled, (state, action) => {
      // Ensure customers is always an array
      if (!Array.isArray(state.customers)) {
        state.customers = [];
      }
      const updatedCustomer = action.payload?.data || action.payload;
      if (updatedCustomer) {
        state.customers = state.customers.map(customer =>
          (customer._id || customer.id)?.toString() === (updatedCustomer._id || updatedCustomer.id)?.toString()
            ? { ...customer, ...updatedCustomer }
            : customer
        );
      }
    });
    builder.addCase(updateCustomer.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteCustomer.fulfilled, (state, action) => {
      // Ensure customers is always an array
      if (!Array.isArray(state.customers)) {
        state.customers = [];
      }
      const customerId = action.payload?.customer || action.payload;
      if (customerId) {
        state.customers = state.customers.filter(
          customer => (customer._id || customer.id)?.toString() !== customerId.toString()
        );
      }
    });
    builder.addCase(deleteCustomer.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    // Categories
    builder.addCase(getEcommerceCategories.fulfilled, (state, action) => {
      state.categories = action.payload?.data || action.payload || [];
      state.error = null; // Clear error on success
    });

    builder.addCase(getEcommerceCategories.rejected, (state, action) => {
      // Handle different error formats
      let errorMessage;
      if (typeof action.payload === 'string') {
        errorMessage = { error: action.payload, message: action.payload };
      } else if (action.payload?.error || action.payload?.message) {
        errorMessage = {
          error: action.payload?.error || action.payload?.message,
          message: action.payload?.message || action.payload?.error
        };
      } else if (action.error?.message) {
        errorMessage = { error: action.error.message, message: action.error.message };
      } else {
        errorMessage = { error: "Failed to load categories", message: "Unknown error occurred" };
      }
      state.error = errorMessage;
      state.categories = []; // Clear categories on error
    });

    builder.addCase(addNewEcommerceCategory.fulfilled, (state, action) => {
      // Ensure categories is always an array
      if (!Array.isArray(state.categories)) {
        state.categories = [];
      }
      const newCategory = action.payload?.data || action.payload;
      if (newCategory) {
        state.categories.unshift(newCategory);
      }
    });

    builder.addCase(addNewEcommerceCategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateEcommerceCategory.fulfilled, (state, action) => {
      // Ensure categories is always an array
      if (!Array.isArray(state.categories)) {
        state.categories = [];
      }
      const updatedCategory = action.payload?.data || action.payload;
      if (updatedCategory) {
        state.categories = state.categories.map(category =>
          (category.id || category._id)?.toString() === (updatedCategory.id || updatedCategory._id)?.toString()
            ? { ...category, ...updatedCategory }
            : category
        );
      }
    });

    builder.addCase(updateEcommerceCategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteEcommerceCategory.fulfilled, (state, action) => {
      if (!state.categories) {
        state.categories = [];
      }
      const categoryId = action.payload?.categoryId || action.payload;
      if (categoryId) {
        state.categories = state.categories.filter(
          category => (category.id || category._id)?.toString() !== categoryId.toString()
        );
      }
    });

    builder.addCase(deleteEcommerceCategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    // Subcategories
    builder.addCase(getSubcategories.fulfilled, (state, action) => {
      state.subcategories = action.payload?.data || action.payload || [];
      state.error = null; // Clear error on success
    });

    builder.addCase(getSubcategories.rejected, (state, action) => {
      const errorMessage = action.payload?.error || action.payload?.message || action.error?.message || action.error || "Failed to load subcategories";
      state.error = typeof errorMessage === 'string' ? { message: errorMessage } : errorMessage;
      state.subcategories = []; // Clear subcategories on error
    });

    builder.addCase(addNewSubcategory.fulfilled, (state, action) => {
      // Ensure subcategories is always an array
      if (!Array.isArray(state.subcategories)) {
        state.subcategories = [];
      }
      const newSubcategory = action.payload?.data || action.payload;
      if (newSubcategory) {
        state.subcategories.unshift(newSubcategory);
      }
    });

    builder.addCase(addNewSubcategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateSubcategory.fulfilled, (state, action) => {
      // Ensure subcategories is always an array
      if (!Array.isArray(state.subcategories)) {
        state.subcategories = [];
      }
      const updatedSubcategory = action.payload?.data || action.payload;
      if (updatedSubcategory) {
        state.subcategories = state.subcategories.map(subcategory =>
          (subcategory.id || subcategory._id)?.toString() === (updatedSubcategory.id || updatedSubcategory._id)?.toString()
            ? { ...subcategory, ...updatedSubcategory }
            : subcategory
        );
      }
    });

    builder.addCase(updateSubcategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteSubcategory.fulfilled, (state, action) => {
      if (!state.subcategories) {
        state.subcategories = [];
      }
      const subcategoryId = action.payload?.subcategoryId || action.payload;
      if (subcategoryId) {
        state.subcategories = state.subcategories.filter(
          subcategory => (subcategory.id || subcategory._id)?.toString() !== subcategoryId.toString()
        );
      }
    });

    builder.addCase(deleteSubcategory.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    // ==================== PRODUCT ATTRIBUTES REDUCERS ====================

    builder.addCase(getAttributes.fulfilled, (state, action) => {
      state.attributes = action.payload?.data || action.payload || [];
    });

    builder.addCase(getAttributes.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.attributes = [];
    });

    builder.addCase(addNewAttribute.fulfilled, (state, action) => {
      if (!Array.isArray(state.attributes)) {
        state.attributes = [];
      }
      const newAttribute = action.payload?.data || action.payload;
      if (newAttribute) {
        state.attributes.unshift(newAttribute);
      }
    });

    builder.addCase(addNewAttribute.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateAttribute.fulfilled, (state, action) => {
      if (!Array.isArray(state.attributes)) {
        state.attributes = [];
      }
      const updatedAttribute = action.payload?.data || action.payload;
      if (updatedAttribute) {
        state.attributes = state.attributes.map(attribute =>
          (attribute.id || attribute._id)?.toString() === (updatedAttribute.id || updatedAttribute._id)?.toString()
            ? { ...attribute, ...updatedAttribute }
            : attribute
        );
      }
    });

    builder.addCase(updateAttribute.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteAttribute.fulfilled, (state, action) => {
      if (!state.attributes) {
        state.attributes = [];
      }
      const attributeId = action.payload?.attributeId || action.payload;
      if (attributeId) {
        state.attributes = state.attributes.filter(
          attribute => (attribute.id || attribute._id)?.toString() !== attributeId.toString()
        );
      }
    });

    builder.addCase(deleteAttribute.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    // ==================== COMMISSIONS REDUCERS ====================

    builder.addCase(getCommissions.fulfilled, (state, action) => {
      state.commissions = action.payload?.data || action.payload || [];
    });

    builder.addCase(getCommissions.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.commissions = [];
    });

    builder.addCase(addNewCommission.fulfilled, (state, action) => {
      if (!Array.isArray(state.commissions)) {
        state.commissions = [];
      }
      const newCommission = action.payload?.data || action.payload;
      if (newCommission) {
        state.commissions.unshift(newCommission);
      }
    });

    builder.addCase(addNewCommission.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateCommission.fulfilled, (state, action) => {
      if (!Array.isArray(state.commissions)) {
        state.commissions = [];
      }
      const updatedCommission = action.payload?.data || action.payload;
      if (updatedCommission) {
        state.commissions = state.commissions.map(commission =>
          (commission.id || commission._id)?.toString() === (updatedCommission.id || updatedCommission._id)?.toString()
            ? { ...commission, ...updatedCommission }
            : commission
        );
      }
    });

    builder.addCase(updateCommission.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteCommission.fulfilled, (state, action) => {
      if (!state.commissions) {
        state.commissions = [];
      }
      const commissionId = action.payload?.commissionId || action.payload;
      if (commissionId) {
        state.commissions = state.commissions.filter(
          commission => (commission.id || commission._id)?.toString() !== commissionId.toString()
        );
      }
    });

    builder.addCase(deleteCommission.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    // ==================== TAXES REDUCERS ====================

    builder.addCase(getTaxes.fulfilled, (state, action) => {
      state.taxes = action.payload?.data || action.payload || [];
    });

    builder.addCase(getTaxes.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
      state.taxes = [];
    });

    builder.addCase(addNewTax.fulfilled, (state, action) => {
      if (!Array.isArray(state.taxes)) {
        state.taxes = [];
      }
      const newTax = action.payload?.data || action.payload;
      if (newTax) {
        state.taxes.unshift(newTax);
      }
    });

    builder.addCase(addNewTax.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(updateTax.fulfilled, (state, action) => {
      if (!Array.isArray(state.taxes)) {
        state.taxes = [];
      }
      const updatedTax = action.payload?.data || action.payload;
      if (updatedTax) {
        state.taxes = state.taxes.map(tax =>
          (tax.id || tax._id)?.toString() === (updatedTax.id || updatedTax._id)?.toString()
            ? { ...tax, ...updatedTax }
            : tax
        );
      }
    });

    builder.addCase(updateTax.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });

    builder.addCase(deleteTax.fulfilled, (state, action) => {
      if (!state.taxes) {
        state.taxes = [];
      }
      const taxId = action.payload?.taxId || action.payload;
      if (taxId) {
        state.taxes = state.taxes.filter(
          tax => (tax.id || tax._id)?.toString() !== taxId.toString()
        );
      }
    });

    builder.addCase(deleteTax.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || action.error || null;
    });
  }
});

export default EcommerceSlice.reducer;